import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent } from '@healthcatalyst/cashmere';
import { AdminStringEditorComponent } from '../string-editor/admin-string-editor.component';
import { StatusService } from '../../shared/services/status.service';
import { PartnerElement, PartnerService } from '../../shared/services/partner.service';

/** Admin partners */
@Component({
    selector: 'pd-admin-partners',
    templateUrl: './admin-partners.component.html',
    styleUrls: ['./admin-partners.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminPartnersComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<PartnerElement> = new HcTableDataSource();
    partnerList: any[] = [];
    loading = true;
    loadError = false;
    adminPartnersDisplayedColumns: string[] = [
        'item',
        'name',
        'status',
        'revisionDate'
    ];
    adminPartnersPageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    statusFilters: string[] = [];
    publishStatusFilters: string[] = [];
    statusText = 'View All';
    publishStatusText = 'View All';
    statusKey: { [key: string]: string } = {};


    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminPartnersListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private partnerService: PartnerService,
        private statusService: StatusService,
        private modalService: ModalService
    ) { }

    ngOnInit(): void {
        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                this.loadPartners();
            } else {
                this.statusService.refresh();
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    loadPartners(): void {
        this.partnerService.partnersUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.partnerList = [];

                // Parse the id fields for table content
                list.forEach(element => {
                    let tempItem = {
                        'item': element.item,
                        'name': element.name,
                        'status': '',
                        'statusID': element.statusID,
                        'revisionDate': element.revisionDate,
                        'partnerID': element.partnerID,
                        'published': element.published
                    };

                    tempItem['status'] = this.formatID( element['statusID'], 'statusID');

                    this.partnerList.push( tempItem );
                });

                this.dataSource = new HcTableDataSource(this.partnerList);
                
                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.partnerService.loadFailed;
                if (!this.loadError) {
                    this.partnerService.refresh();
                } else {
                    this.loading = false;
                }
            }
        })
    }

    applySearchFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'status':
                if (filterArray.length == 1) this.statusText = filterArray[0];
                else if (filterArray.length > 1) this.statusText = `${filterArray.length} selected`;
                else this.statusText = 'View All';
                break;
            case 'published':
                if (filterArray.length == 1) this.publishStatusText = filterArray[0];
                else if (filterArray.length > 1) this.publishStatusText = `${filterArray.length} selected`;
                else this.publishStatusText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        //add clean strings for publish status
        if (type === 'published') {
            filterData.push('Published');
            filterData.push('Unpublished');
        } else {
            for (let i = 0; i < this.partnerList.length; i++) {
                if (this.partnerList[i][type]) {
                    if (!filterData.includes(this.partnerList[i][type])) {
                        filterData.push(this.partnerList[i][type]);
                    }
                }
            }
        }
        this.filterData = filterData;
    }

    filterPartners() {
        let filteredPartners: PartnerElement[] = [];

        for (let i = 0; i < this.partnerList.length; i++) {
            let statusFilterMatch = this.statusFilters.length === 0;
            if (this.statusFilters.includes(this.partnerList[i]['status'])) {
                statusFilterMatch = true;
            }

            // check string filter values from filter popoover against boolean values from data
            let publishStatusFilterMatch = this.publishStatusFilters.length === 0;
            if (
                (this.publishStatusFilters.includes('Published') && this.partnerList[i]['published'] === true) ||
                (this.publishStatusFilters.includes('Unpublished') && this.partnerList[i]['published'] === false)
            ) {
                publishStatusFilterMatch = true;
            }

            if (statusFilterMatch && publishStatusFilterMatch) {
                filteredPartners.push(this.partnerList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered partner list
        this.dataSource = new HcTableDataSource(filteredPartners);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);
        
        if (this.filterType === 'status') {
            this.statusFilters = filterArray;
        } else if (this.filterType === 'published') {
            this.publishStatusFilters = filterArray;
        }

        this.filterPartners();
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    openStringEditor(type: string) {
        const options: ModalOptions = {
            closeIcon: true,
            size: 'md',
            data: {
                type: type,
                category: 'Partner'
            }
        };

        this.modalService.open(AdminStringEditorComponent, options);
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if (type === 'statusID') {
            reformattedID = this.statusKey[id.toString()];
        }

        return reformattedID
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
